import { countBy, mapValues } from "lodash";
import moment from "moment";

import {
  getAccount,
  getIgnitionSession,
  getIgnitionSessions,
  getPolicies,
  getPolicy,
} from "../automatic";
import { appendClientIdToPath } from "../utility";

/** The most rows a search hands Claude; the count still covers every match. */
const MAX_ROWS = 50;

const DEFAULT_IGNITION_DAYS = 90;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

type Lookup = { name: string; input: Record<string, any> };

const includes = (value: unknown, search?: string) =>
  !search ||
  String(value ?? "")
    .toLowerCase()
    .includes(search.toLowerCase());

const fullName = (person?: { firstName?: string; lastName?: string }) =>
  [person?.firstName, person?.lastName].filter(Boolean).join(" ");

// The calls that return [data, error] rather than throwing.
const unwrap = ([data, error]: any[]) => {
  if (error) throw new Error(error.message);
  return data;
};

// Counts cover every match, so a breakdown never stops at the rows shown.
const summarise = <T, R>(
  matches: T[],
  toRow: (match: T) => R,
  countFields: Record<string, (match: T) => unknown>
) => ({
  matched: matches.length,
  countsBy: mapValues(countFields, (field) =>
    countBy(matches, (match) => String(field(match) ?? "unknown"))
  ),
  shown: Math.min(matches.length, MAX_ROWS),
  rows: matches.slice(0, MAX_ROWS).map(toRow),
});

/** Filters the client's policies the way search_policies describes. */
export const searchPolicies = (
  policies: any[],
  input: Record<string, any>,
  clientId: string
) =>
  summarise(
    policies.filter(
      (policy) =>
        (input.is_active === undefined ||
          policy.isActive === input.is_active) &&
        includes(policy.carrier, input.carrier) &&
        includes(policy.policyNumber, input.policy_number) &&
        (!input.insured_last_name ||
          (policy.insureds ?? []).some((insured: any) =>
            includes(insured?.lastName, input.insured_last_name)
          )) &&
        (!input.created_after ||
          moment(policy.createdAt).isSameOrAfter(input.created_after)) &&
        (!input.created_before ||
          moment(policy.createdAt).isBefore(input.created_before))
    ),
    (policy) => ({
      id: policy.id,
      policyNumber: policy.policyNumber,
      carrier: policy.carrier,
      type: policy.type,
      isActive: policy.isActive,
      insureds: (policy.insureds ?? []).map(fullName).join(", "),
      effectiveDate: policy.effectiveDate,
      expirationDate: policy.expirationDate,
      account: policy.account,
      createdAt: policy.createdAt,
      dashboardPath: appendClientIdToPath(`/policies/${policy.id}`, clientId),
    }),
    {
      isActive: (policy) => policy.isActive,
      carrier: (policy) => policy.carrier,
      type: (policy) => policy.type,
    }
  );

/** Filters ignition sessions the way search_ignition_sessions describes. */
export const searchIgnitionSessions = (
  sessions: any[],
  input: Record<string, any>,
  clientId: string
) =>
  summarise(
    sessions.filter(
      (session) =>
        (!input.status || session.status === input.status) &&
        includes(session.user?.lastName, input.user_last_name) &&
        includes(session.user?.email, input.user_email)
    ),
    (session) => ({
      ignitionToken: session.id,
      status: session.status,
      result: session.result,
      carrier: session.carrier,
      user: fullName(session.user),
      email: session.user?.email,
      account: session.account,
      createdAt: session.createdAt,
      dashboardPath: appendClientIdToPath(`/ignition/${session.id}`, clientId),
    }),
    {
      status: (session) => session.status,
      carrier: (session) => session.carrier,
    }
  );

/**
 * Runs a lookup Axle AI asked for through the same calls the dashboard's
 * pages make, so it sees exactly what the selected client sees there. Returns
 * what Claude reads back; throws when the call fails.
 */
export const runLookup = async ({ name, input }: Lookup, clientId: string) => {
  switch (name) {
    case "get_policy":
      return unwrap(await getPolicy(input.policy_id, {}, clientId));
    case "get_account":
      return unwrap(
        await getAccount(input.account_id, { expand: true }, clientId)
      );
    case "get_ignition_session":
      return unwrap(await getIgnitionSession(input.ignition_token, clientId));
    case "search_policies":
      return searchPolicies(await getPolicies(clientId), input, clientId);
    case "search_ignition_sessions": {
      const start =
        input.start ??
        moment().subtract(DEFAULT_IGNITION_DAYS, "days").toISOString();
      const end = input.end ?? moment().toISOString();
      // A date-only end means through that day, as the Ignition page's picker does.
      const endMoment = DATE_ONLY.test(end)
        ? moment(end).endOf("day")
        : moment(end);
      const sessions = await getIgnitionSessions(
        {
          start: moment(start).toISOString(),
          end: endMoment.toISOString(),
        },
        clientId
      );
      return {
        start,
        end,
        ...searchIgnitionSessions(sessions, input, clientId),
      };
    }
    default:
      throw new Error(`Unknown lookup ${name}`);
  }
};
